import React, { useRef, useState } from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import { useLoginContext } from '../../Contexts/LoginContext';
import { useStoreContext } from '../../Contexts/StoreContext';
import ButtonPrimary from './ButtonPrimary';
import Button from './Button';
import { HiXMark } from 'react-icons/hi2';
import { IMaskInput } from 'react-imask';
import { TbCircleFilled, TbQuestionMark, TbX } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { goScrollTo, hideKeyboard, isCpfValid } from '../../Utils/Helpers';
import { ImSpinner8 } from 'react-icons/im';
// import { BiCheckbox, BiSolidCheckboxChecked } from "react-icons/bi";
import { formatZipcode } from '../../Utils/Formatters';
import ButtonLink from './ButtonLink';
import { useTranslation } from 'react-i18next';
import { BiCheckbox, BiSolidCheckboxChecked } from 'react-icons/bi';
import { useCheckoutContext } from '../../Contexts/CheckoutContext';
import { ALL_STATES } from '../../Utils/Constants/Global';

const currentYear = new Date().getFullYear();
const lastTwoDigitsOfCurrentYear = String(currentYear).slice(-2);
const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');

const getValidationSchema = (t: any) => 
    Yup.object().shape({
        name: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .required(t('form.validation.required')),
        number: Yup.string()
            .min(16, t("form.validation.invalid_min_characters", {quantity: 16}))
            .required(t('form.validation.required')),
        expirationFull: Yup.string()
            .test('len', t('form.validation.invalid'), value => {
                if (!value || value.length !== 4) {
                    return false;
                }
                const month = value.substring(0, 2);
                const year = value.substring(2, 4);
                const isValidMonth = month >= '01' && month <= '12';
                const isValidYear = year >= lastTwoDigitsOfCurrentYear;
                const isValidCurrentMonth =
                    (year === lastTwoDigitsOfCurrentYear && month >= currentMonth) ||
                    (year > lastTwoDigitsOfCurrentYear);
                return isValidMonth && isValidYear && isValidCurrentMonth;
            })
            .required(t('form.validation.required')),
        cvc: Yup.string()
            .min(3, t("form.validation.invalid_min_characters", {quantity: 3}))
            .required(t('form.validation.required')),
        holder_document: Yup.string()
            .test('document-length',
                function (value) {
                    if (!value) return true;

                    const clean = value.replace(/\D/g, '');
                    if (clean.length < 11) {
                        return this.createError({
                            message: t('form.validation.incomplete', { label: 'CPF' })
                        });
                    }
                    if (clean.length > 11 && clean.length < 14) {
                        return this.createError({
                            message: t('form.validation.incomplete', { label: 'CNPJ' })
                        });
                    }
                    if (clean.length > 14) {
                        return this.createError({
                            message: t('form.validation.invalid')
                        });
                    }
                    return true;
                }
            )
            .test('cpf-valid',
                function (value) {
                    if (!value) return true;
                    const clean = value.replace(/\D/g, '');
                    // só valida CPF (11 dígitos)
                    if (clean.length === 11 && !isCpfValid(clean)) {
                        return this.createError({
                        message: t('form.validation.invalid')
                        });
                    }
                    return true;
                }
            )
            .required(t('form.validation.required')),
        billing_zip_code: Yup.string()
            .min(8, t("form.validation.invalid_min_characters", {quantity: 8}))
            .required(t('form.validation.required')),
        billing_state: Yup.string()
            .max(2, t("form.validation.invalid_exact_quantity", {quantity: 2}))
            .required(t('form.validation.required')),
        billing_city: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .required(t('form.validation.required')),
        billing_street: Yup.string().required(t('form.validation.required')),
        billing_neighborhood: Yup.string().required(t('form.validation.required')),
});

interface FormRegisterCustomerCardProps {
    onCancel: CallableFunction;
    onSetCard: CallableFunction;
    onCopyDocument?: CallableFunction;
}

const FormRegisterCustomerCard = ({
    onCancel,
    onSetCard = () => {},
    onCopyDocument = () => {}
    }: FormRegisterCustomerCardProps) => {

    const { t } = useTranslation();
    const validationSchema = getValidationSchema(t);
    const { orderType, isStoreCounter, isDelivery } = useOrderTypeContext();
    const { user } = useLoginContext();
    const { currentCard } = useCheckoutContext();
    const { currentStore, currentAddress } = useStoreContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconInfo, setMainLoadingIconError, setMainLoadingIconNoSearchMap, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton } = useLayoutContext();
    const [loadingZipcode, setLoadingZipcode] = useState(false)
    const [showFieldsAddress, setShowFieldsAddress] = useState<boolean>(Boolean(currentCard))
    const isFirstRender = useRef(true);

    const initialCardData = {
        name: currentCard?.name || '',
        number: currentCard?.number || '',
        expiration_month: currentCard?.expiration_month || '',
        expiration_year: currentCard?.expiration_year || '',
        cvc: currentCard?.cvc || '',
        holder_document: currentCard?.holder_document || '',
        billing_country: 'br',
        billing_zip_code: currentCard?.billing_zip_code || '',
        billing_state: currentCard?.billing_state || '',
        billing_city: currentCard?.billing_city || '',
        billing_neighborhood: currentCard?.billing_neighborhood || '',
        billing_street: currentCard?.billing_street || '',
        billing_street_number: currentCard?.billing_street_number || '',
        store_id: currentStore.id,
        main: true,
        expirationFull: currentCard?.expiration_month + currentCard?.expiration_year || '',
        save_card: user?.external_id
    }

    const handleInfoCvc = () => {
        setMainLoadingIcon(false);
        setMainLoadingIconInfo(true);
        setMainLoading(true);
        setMainLoadingTitle(t('checkout.cvc.title'));
        setMainLoadingMessage(t('checkout.cvc.message'));
        setMainLoadingCloseButton(true);
    }

    const handleRegisterCard = async (cardData: any) => {
        const payload = {
            name: cardData.name.toUpperCase(),
            number: cardData.number,
            expiration_month: cardData.expiration_month,
            expiration_year: cardData.expiration_year,
            cvc: cardData.cvc,
            holder_document: cardData.holder_document,
            billing_country: cardData.billing_country,
            billing_zip_code: cardData.billing_zip_code,
            billing_state: cardData.billing_state,
            billing_city: cardData.billing_city,
            billing_neighborhood: cardData.billing_neighborhood,
            billing_street: cardData.billing_street,
            billing_street_number: cardData.billing_street_number,
            store_id: currentStore.id,
            main: true
        }

        //* PAGAMENTO SEM REGISTRO DO CARTÃO
        if (!cardData.save_card) {
            onSetCard(payload);
        } else {
            //* PAGAMENTO COM REGISTRO DO CARTÃO
            setMainLoading(true);
            setMainLoadingTitle(t('checkout.form_register_customer_card.save_card.loading_title'));
            setMainLoadingMessage(t('layout.loading_main.default_message'));
            try {
                const customerService = new CustomerService(orderType);
                const registeredCard = isStoreCounter
                    ? await customerService.registerCardStoreCounter(payload)
                    : await customerService.registerCard(user.id, payload)

                if (registeredCard) {
                    const payload = {
                        customer_card_id: registeredCard.id,
                        customer_card_cvv: cardData.cvc,
                        number: cardData.number
                    }
                    onSetCard(payload);
                    setMainLoading(false);
                }
            } catch (error) {
                setMainLoadingIcon(false);
                setMainLoadingIconError(true);
                setMainLoadingTitle(t('errors.default_title'));
                setMainLoadingMessage(
                    isStoreCounter
                        ? t('checkout.form_register_customer_card.error.store_counter')
                        : t('checkout.form_register_customer_card.error.default')
                );
                setMainLoadingCloseButton(true)
            }
        }
    }

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field.tests.some((test: any) => test.name === 'required');
    }

    return (
        <>
            <Formik
                initialValues={initialCardData}
                validationSchema={validationSchema}
                validateOnMount={true} // forçando a validação na montagem do formulário
                validateOnChange={true}
                validateOnBlur={true}
                onSubmit={(cardData) => handleRegisterCard(cardData)}
                >
                  {({ setFieldValue, values, validateForm, handleSubmit, isValid }) => (
                    <Form className="flex flex-col gap-4 justify-between h-full gap-4"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            handleSubmit();

                            const errors = await validateForm();

                            if (Object.keys(errors).length > 0) {
                                const firstErrorKey = Object.keys(errors)[0];
                                const el = document.querySelector(`[name="${firstErrorKey}"]`) as HTMLElement;
                                if (el) goScrollTo(el); // rolar até o erro
                            }
                        }}
                        >

                        <div className='h-full flex flex-col gap-3 p-4 pt-2'>

                            <div className='grid gap-2'>
                                <div className='flex gap-2'>
                                    <div className='w-full'>
                                        <span className='text-xs text-gray-500'>
                                            {t('checkout.form_register_customer_card.name.label')}
                                            {isFieldRequired(validationSchema, 'name') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                        </span>
                                        <IMaskInput
                                            id="name"
                                            name="name"
                                            autoComplete='on'
                                            mask={/^[a-zA-ZÀ-ÿ\s]*$/}
                                            value={values.name}
                                            onAccept={(value) => setFieldValue('name', value)} // Atualiza o valor no Formik
                                            unmask={true} // true|false|'typed'
                                            placeholder={t('checkout.form_register_customer_card.name.placeholder')}
                                            className="relative uppercase block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                        />
                                        <ErrorMessage name="name" component="div" className='text-xs text-shopfood-error pt-1' />
                                    </div>
                                    <div className='w-32'>
                                        <label htmlFor="expirationFull" className='text-xs text-gray-500'>
                                            {t('checkout.form_register_customer_card.expiration_full.label')}
                                            {isFieldRequired(validationSchema, 'expirationFull') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                        </label>
                                        <IMaskInput
                                            id="expirationFull"
                                            name="expirationFull"
                                            inputMode="numeric"
                                            mask={"00 / 00"}
                                            value={values.expirationFull}
                                            onAccept={(value) => {
                                                if (value.length === 4) {
                                                    setFieldValue('expiration_month', value.slice(0, 2))
                                                    setFieldValue('expiration_year', value.slice(2))
                                                } else {
                                                    setFieldValue('expiration_month', '')
                                                    setFieldValue('expiration_year', '')
                                                }
                                                setFieldValue('expirationFull', value);
                                            }}
                                            unmask={true} // true|false|'typed'
                                            placeholder='-- / --'
                                            className='relative block w-full autofill:bg-yellow-600 rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-200'
                                        />
                                        <ErrorMessage name="expirationFull" component="div" className='text-xs text-shopfood-error pt-1' />
                                    </div>
                                </div>

                                <div className='flex gap-2'>
                                    <div className='w-full'>
                                        <label htmlFor="number" className='text-xs text-gray-500'>
                                            {t('checkout.form_register_customer_card.number.label')}
                                            {isFieldRequired(validationSchema, 'number') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                        </label>
                                        <div className='relative flex items-center justify-between'>
                                            <IMaskInput
                                                id="number"
                                                name="number"
                                                inputMode="numeric"
                                                mask={"0000 0000 0000 0000"}
                                                value={values.number}
                                                onAccept={(value) => setFieldValue('number', value)}
                                                unmask={true}
                                                placeholder='---- ---- ---- ----'
                                                className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                            />
                                        </div>
                                        <ErrorMessage name="number" component="div" className='text-xs text-shopfood-error pt-1' />
                                    </div>
                                    <div className='w-32'>
                                        <label htmlFor="cvc" className='text-xs text-gray-500'>
                                            {t('checkout.form_register_customer_card.cvc.label')}
                                            {isFieldRequired(validationSchema, 'cvc') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                        </label>
                                        <div className='relative flex items-center justify-between'>
                                            <IMaskInput
                                                id="cvc"
                                                name="cvc"
                                                inputMode="numeric"
                                                mask={"0000"}
                                                value={values.cvc}
                                                onAccept={(value) => setFieldValue('cvc', value)}
                                                unmask={true}
                                                placeholder='---'
                                                className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                            />
                                            <TbQuestionMark size={22} onClick={handleInfoCvc} className='absolute cursor-pointer p-1 right-2 bg-gray-100 hover:bg-gray-200 rounded-full' />
                                        </div>
                                        <ErrorMessage name="cvc" component="div" className='text-xs text-shopfood-error pt-1' />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="holder_document" className='text-xs text-gray-500'>
                                        {t('checkout.form_register_customer_card.holder_document.label')}
                                        {isFieldRequired(validationSchema, 'holder_document') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </label>
                                    <div className="relative flex items-center justify-center w-full">
                                        <IMaskInput
                                            id="holder_document"
                                            name="holder_document"
                                            inputMode="numeric"
                                            maxLength={14}
                                            mask={"000.000.000-00"}
                                            // mask={[
                                            //     { mask: '000.000.000-00', maxLength: 11 },  // CPF
                                            //     // { mask: '00.000.000/0000-00' }  // CNPJ
                                            // ]}
                                            value={values.holder_document}
                                            onAccept={(value) => setFieldValue('holder_document', value)}
                                            unmask={true}
                                            placeholder='---.---.--- --'
                                            className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                        />
                                        {values.holder_document && isCpfValid(values.holder_document)
                                            ?  ''
                                            :  values.holder_document
                                                ? <HiXMark size={22} onClick={() => setFieldValue('holder_document', '')} className='absolute cursor-pointer p-1 right-2 bg-gray-100 hover:bg-gray-200 rounded-full' />
                                                : user?.cpf && <ButtonLink onClick={() => {
                                                    setFieldValue('holder_document', user.cpf);
                                                    onCopyDocument();
                                                }} className="absolute right-2 text-xs font-medium h-8">{t('checkout.use_my_document')}</ButtonLink>
                                        }
                                    </div>
                                    <ErrorMessage name="holder_document" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>


                                <div className={`${isDelivery ? 'w-full' : 'w-48'}`}>
                                    <label htmlFor="billing_zip_code" className='text-xs text-gray-500'>
                                        {t('checkout.form_register_customer_card.billing_zip_code.label')}
                                        {isFieldRequired(validationSchema, 'billing_zip_code') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </label>
                                    <div className='relative flex items-center justify-between'>
                                        <IMaskInput
                                            id="billing_zip_code"
                                            name="billing_zip_code"
                                            inputMode="numeric"
                                            mask={'00000-000'}
                                            value={values.billing_zip_code}
                                            onAccept={(value) => {
                                                if (value.length === 8) {
                                                    hideKeyboard();

                                                    // Verifica se é o primeiro render e se tem um cartão atual no state (usado na navegação)
                                                    if (currentCard && isFirstRender.current) {
                                                        isFirstRender.current = false;
                                                        setFieldValue('billing_street', currentCard.billing_street);
                                                        setFieldValue('billing_street_number', currentCard.billing_street_number);
                                                        setFieldValue('billing_neighborhood', currentCard.billing_neighborhood);
                                                        setFieldValue('billing_city', currentCard.billing_city);
                                                        setFieldValue('billing_state', currentCard.billing_state);
                                                        return;
                                                    }

                                                    setLoadingZipcode(true);
                                                    fetch(`https://ceps.shopfood.io/addresses/search?q=${value}`)
                                                        .then(response => {
                                                            if (!response.ok) {
                                                                throw new Error('Network response was not ok');
                                                            }
                                                            return response.json();
                                                        })
                                                        .then(data => {
                                                            if (Array.isArray(data) && data.length > 0) {
                                                                const dataResp = data[0];
                                                                setFieldValue('billing_state', dataResp.state);
                                                                setFieldValue('billing_city', dataResp.city);
                                                                setFieldValue('billing_neighborhood', dataResp.neighborhood);
                                                                setFieldValue('billing_street', dataResp.street);
                                                                setFieldValue('billing_street_number', '');
                                                            }
                                                            else {
                                                                setMainLoadingIcon(false);
                                                                setMainLoadingIconNoSearchMap(true);
                                                                setMainLoading(true);
                                                                setMainLoadingTitle(t('checkout.form_register_customer_card.billing_zip_code.error.not_found_title', {zipcode: formatZipcode(value) }));
                                                                setMainLoadingMessage(t('checkout.form_register_customer_card.billing_zip_code.error.not_found_message'));
                                                                setMainLoadingCloseButton(true);
                                                            }
                                                            setShowFieldsAddress(true);
                                                        })
                                                        .catch(error => {})
                                                        .finally(() => {
                                                            setLoadingZipcode(false);
                                                            setTimeout(() => {
                                                                validateForm();
                                                            }, 100);
                                                        });
                                                } else {
                                                    setFieldValue('billing_state', '');
                                                    setFieldValue('billing_city', '');
                                                    setFieldValue('billing_neighborhood', '');
                                                    setFieldValue('billing_street', '');
                                                    setShowFieldsAddress(false);
                                                }
                                                setFieldValue('billing_zip_code', value)
                                            }}
                                            unmask={true} // true|false|'typed'
                                            placeholder="----- ---"
                                            className="relative uppercase block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                        />
                                        {loadingZipcode
                                                ? <ImSpinner8 size={22} className="animate-spin absolute p-1 right-2 rounded-full transition-all ease-in-out duration-200" />
                                                : values.billing_zip_code 
                                                    ? <TbX size={22} onClick={() => setFieldValue('billing_zip_code', '')} className='absolute cursor-pointer p-1 right-2 hover:bg-gray-200 rounded-full  transition-all ease-in-out duration-200' />
                                                    : isDelivery 
                                                        ? <ButtonLink onClick={() => setFieldValue('billing_zip_code', currentAddress?.zipcode)} className="absolute right-2 text-xs font-medium h-8">{t('checkout.use_zipcode_current_address')}</ButtonLink>
                                                        : null
                                        }
                                    </div>
                                    <ErrorMessage name="billing_zip_code" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>

                                {showFieldsAddress &&
                                    <>
                                        <div className='flex gap-2'>
                                            <div className='w-full'>
                                                <label htmlFor="billing_street" className='text-xs text-gray-500'>
                                                    {t('checkout.form_register_customer_card.billing_street.label')}
                                                    {isFieldRequired(validationSchema, 'billing_street') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                                </label>
                                                <input
                                                    id="billing_street"
                                                    name="billing_street"
                                                    placeholder=""
                                                    value={values.billing_street}
                                                    onChange={e => setFieldValue('billing_street', e.target.value)}
                                                    disabled={loadingZipcode}
                                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 disabled:text-gray-400 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                                />
                                                <ErrorMessage name="billing_street" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>

                                            <div className='w-32'>
                                                <label htmlFor="billing_street_number" className='text-xs text-gray-500'>
                                                    {t('checkout.form_register_customer_card.billing_street_number.label')}
                                                </label>
                                                <input
                                                    id="billing_street_number"
                                                    name="billing_street_number"
                                                    placeholder=""
                                                    value={values.billing_street_number}
                                                    onChange={e => setFieldValue('billing_street_number', e.target.value)}
                                                    disabled={loadingZipcode}
                                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 disabled:text-gray-400 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                                />
                                                <ErrorMessage name="billing_street_number" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>
                                        </div>

                                        <div className='w-full'>
                                            <label htmlFor="billing_neighborhood" className='text-xs text-gray-500'>
                                                {t('checkout.form_register_customer_card.billing_neighborhood.label')}
                                                {isFieldRequired(validationSchema, 'billing_neighborhood') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                            </label>
                                            <input
                                                id="billing_neighborhood"
                                                name="billing_neighborhood"
                                                placeholder=""
                                                value={values.billing_neighborhood}
                                                onChange={e => setFieldValue('billing_neighborhood', e.target.value)}
                                                disabled={loadingZipcode}
                                                className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 disabled:text-gray-400 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                            />
                                            <ErrorMessage name="billing_neighborhood" component="div" className='text-xs text-shopfood-error pt-1' />
                                        </div>

                                        <div className='flex gap-2'>
                                            <div className='w-full'>
                                                <label htmlFor="billing_city" className='text-xs text-gray-500'>
                                                    {t('checkout.form_register_customer_card.billing_city.label')}
                                                    {isFieldRequired(validationSchema, 'billing_city') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                                </label>
                                                <input
                                                    id="billing_city"
                                                    name="billing_city"
                                                    placeholder=""
                                                    value={values.billing_city}
                                                    onChange={e => setFieldValue('billing_city', e.target.value)}
                                                    disabled={loadingZipcode}
                                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 disabled:text-gray-400 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                                />
                                                <ErrorMessage name="billing_city" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>

                                            <div className='w-32'>
                                                <label htmlFor="billing_state" className='text-xs text-gray-500'>
                                                    {t('checkout.form_register_customer_card.billing_state.label')}
                                                    {isFieldRequired(validationSchema, 'billing_state') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                                </label>
                                                <select
                                                    name="billing_state"
                                                    value={values.billing_state}
                                                    disabled={loadingZipcode}
                                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFieldValue('billing_state', e.target.value)}
                                                    className="relative block uppercase w-full rounded border-0 p-2.5 py-3 ring-1 ring-gray-300 disabled:text-gray-400 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                                    >
                                                    <option value="">UF</option>
                                                    {ALL_STATES.map((uf: string) => (
                                                        <option key={uf} value={uf}>{uf}</option>
                                                    ))}
                                                </select>
                                                <ErrorMessage name="billing_state" component="div" className='text-xs text-shopfood-error pt-1' />
                                            </div>

                                        </div>
                                    </>
                                }

                                {user && user.external_id &&
                                    <div className='flex items-center pt-2 text-xs -ml-1'>
                                        {
                                            values.save_card
                                                ?   <BiSolidCheckboxChecked size={25} className='text-custom-primary cursor-pointer' onClick={() => setFieldValue('save_card', false)} />
                                                :   <BiCheckbox size={25} className='text-gray-400 cursor-pointer' onClick={() => setFieldValue('save_card', true)} />
                                        }
                                        <label
                                            htmlFor='save_card'
                                            onClick={() => setFieldValue('save_card', !values.save_card)}
                                            className={`cursor-pointer transition
                                            ${values.save_card ? 'font-semibold text-custom-primary' : 'text-shopfood-title'}`}
                                        >{t('checkout.save_card_label')}</label>
                                    </div>
                                }

                            </div>
                        </div>

                        <div className='flex items-center gap-2 p-4 w-full'>
                            <ButtonPrimary type='submit' className='w-full xs:max-w-sm md:max-w-xs' onClick={() => {}}>{t('cart.button_continue_payment_label')}</ButtonPrimary>
                            <Button rounded onClick={() => onCancel()}>
                                <div><HiXMark size={20} /></div>
                            </Button>
                        </div>

                    </Form>

                )}
            </Formik>
        </>
    )
}

export default FormRegisterCustomerCard;
